import React from "react";
import { Link } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import { CORE_PILLARS, SERVICES } from "@/data/site";
import { ArrowUpRight, Check } from "lucide-react";
import Seo from "@/components/Seo";
import BrochureCta from "@/components/BrochureCta";

export default function Services() {
  // group services by category
  const grouped = SERVICES.reduce((acc, s) => {
    (acc[s.category] = acc[s.category] || []).push(s);
    return acc;
  }, {});

  return (
    <div data-testid="page-services">
      <Seo
        path="/services"
        title="Services & Capabilities"
        description="Full catalogue: lithium-ion & LiFePO4 battery manufacturing, customised battery packs, solar street lights, EV assembling, traffic-signal & weighing-scale batteries, pole fabrication, inverters and CSR projects."
      />
      <PageHeader
        eyebrow="Services"
        title="Full-stack energy services."
        subtitle="From a single 12V LiFePO4 pack to a 24-pole solar lighting EPC contract — we deliver across batteries, solar, EVs, inverters and community projects."
        testId="services-page-header"
      />

      {/* CORE PILLARS expanded */}
      <section className="py-16 md:py-24 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-16">
          {CORE_PILLARS.map((p, idx) => (
            <div
              key={p.id}
              id={p.id}
              className={`grid grid-cols-1 lg:grid-cols-12 gap-8 items-center ${
                idx % 2 === 1 ? "lg:[direction:rtl]" : ""
              }`}
              data-testid={`service-pillar-${p.id}`}
            >
              <div className="lg:col-span-6 [direction:ltr]">
                <div className="industrial-card overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.title}
                    className="w-full h-[320px] md:h-[420px] object-cover"
                  />
                </div>
              </div>
              <div className="lg:col-span-6 [direction:ltr]">
                <div className="eyebrow">0{idx + 1} · Core pillar</div>
                <h2 className="font-display text-3xl md:text-5xl uppercase mt-3 tracking-tighter leading-[0.95]">
                  {p.title}
                </h2>
                <div className="mt-4 tag-chip tag-chip-red">{p.spec}</div>
                <p className="mt-5 text-zinc-300 leading-relaxed">
                  {p.description}
                </p>
                <ul className="mt-6 space-y-2 text-sm text-zinc-300">
                  {[
                    "Custom sizing & spec advisory",
                    "Pre-shipment QC & burn-in",
                    "Documentation & GST invoicing",
                    "Installation + AMC available",
                  ].map((b) => (
                    <li key={b} className="flex items-start gap-2">
                      <Check size={14} className="text-[#e63946] mt-1" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/contact" className="btn-primary mt-7" data-testid={`service-pillar-cta-${p.id}`}>
                  Discuss your requirement <ArrowUpRight size={16} />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Full catalogue grouped */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="eyebrow">Full catalogue</div>
          <h2 className="font-display text-3xl md:text-5xl uppercase mt-3 tracking-tighter">
            All services we offer.
          </h2>

          <div className="mt-12 space-y-12">
            {Object.entries(grouped).map(([cat, items]) => (
              <div key={cat} data-testid={`service-group-${cat.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="flex items-end justify-between border-b border-white/10 pb-3 mb-5">
                  <h3 className="font-display text-xl md:text-2xl uppercase tracking-tight">
                    {cat}
                  </h3>
                  <span className="font-mono text-xs text-zinc-500 tracking-[0.2em]">
                    {String(items.length).padStart(2, "0")} services
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {items.map((s) => (
                    <div
                      key={s.name}
                      className="industrial-card p-5 flex items-center justify-between"
                    >
                      <div className="text-sm font-medium">{s.name}</div>
                      <ArrowUpRight size={14} className="text-zinc-600" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16">
            <BrochureCta testId="services-brochure-cta" />
          </div>
        </div>
      </section>
    </div>
  );
}
